import { Stack, Typography } from "@mui/material";
import type {
  DataGridPremiumProps,
  GridColDef,
  GridGroupingColDefOverride,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { GridCellCheckboxRenderer, useGridApiRef } from "@mui/x-data-grid-premium";
import type { PropsWithChildren } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import { getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper.ts";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import {
  checkSelection,
  getRowNode,
  setAllNonGroupRowsSelection,
} from "@entrilia/shared/utilities/dataGridApiHelper.ts";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { ClientInfoExtended, ReportTemplate, ReportTemplateDto } from "../../../../../../api/types/svTypes.ts";
import { Status } from "../../../../../../api/types/svTypes.ts";
import GroupHeaderWithSelection from "../../../../../common/grid/GroupHeaderWithSelection";
import ReportTemplateUiManagerContext from "../../contexts/template/ReportTemplatesUiManagerContext";
import GroupName from "../../reports/grid/GroupName";
import useGridExpand from "../../reports/hooks/useGridExpand";
import { getEditReportTemplateUrl } from "../../reports/utilities/editReportUrl";
import RunWithCompanyDialog from "../RunWithCompanyDialog.tsx";
import ActionMenuButton from "../actionMenu/ActionMenuButton";
import { formatFundType } from "../helpers/reportTemplateHelper.ts";
import DraftStatus from "../status/DraftStatus.tsx";
import LiveStatus from "../status/LiveStatus.tsx";
import OrganizationTemplateGridCustomHeader from "./OrganizationTemplateGridCustomHeader";

interface Props {
  templates: ReportTemplateDto[];
  companies: ClientInfoExtended[];
  readonly?: boolean;
}

const getRowId = (row: ReportTemplateDto) => row.reportId;

const isRowSelectable = ({ row }: GridRowParams<ReportTemplateDto>): boolean => !!row.reportId;

export default function OrganizationTemplatesGrid({ templates, companies, readonly }: Props) {
  const [currentTemplateId, setCurrentTemplateId] = useState<string>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { deleteTemplates } = useDefinedContext(ReportTemplateUiManagerContext);

  const sortModelRef = useRef<GridSortModel>(undefined);
  const apiRef = useGridApiRef();
  const { isGroupExpandedByDefault, toggleSingleGridRow, toggleAllGridRows } = useGridExpand({
    gridKey: "ORGANIZATION_REPORT_TEMPLATES_GRID",
    apiRef,
  });

  const sortedTemplates = useMemo(() => [...templates].sort((a, b) => a.groupOrder - b.groupOrder), [templates]);
  const showToolbar = useMemo(() => selectedIds.length > 0, [selectedIds]);
  const fullSelection = checkSelection(apiRef).checked;

  const groupColumnDef = useMemo(
    (): GridGroupingColDefOverride => ({
      hideDescendantCount: true,
      headerName: "Name",
      sortable: true,
      aggregable: false,
      hideable: false,
      pinnable: false,
      filterable: false,
      flex: 1,
      sortComparator: (_v1, _v2, row1, row2) => {
        const direction = sortModelRef.current?.[0]?.sort ?? "asc";
        if (row1.rowNode.type === "group" && row2.rowNode.type === "group") {
          const firstGroupOrder = templates.find((r) => r.groupName === row1.value)?.groupOrder || 0;
          const secondGroupOrder = templates.find((r) => r.groupName === row2.value)?.groupOrder || 0;
          return direction === "asc" ? firstGroupOrder - secondGroupOrder : secondGroupOrder - firstGroupOrder;
        }
        const firstTemplateName = templates.find((r) => r.reportId === row1.rowNode.id)?.name || "";
        const secondTemplateName = templates.find((r) => r.reportId === row2.rowNode.id)?.name || "";
        return firstTemplateName.localeCompare(secondTemplateName);
      },
      renderHeader: (params) => (
        <GroupHeaderWithSelection
          headerName={params.colDef.headerName}
          permissions={["ManageOrganizationReportTemplates"]}
          onExpandToggle={toggleAllGridRows}
        />
      ),
      renderCell: (params: GridRenderCellParams<ReportTemplate, unknown, unknown>) => {
        if (params.rowNode.type === "group") {
          return (
            <GroupName
              params={params}
              permissions={["ManageOrganizationReportTemplates"]}
              onExpandToggle={toggleSingleGridRow}
            />
          );
        }
        return (
          <NameColumn template={params.row} readonly={readonly} companies={companies}>
            <GridCellCheckboxRenderer {...params} />
          </NameColumn>
        );
      },
    }),
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [templates, selectedIds, toggleSingleGridRow, toggleAllGridRows]
  );

  const columns = useMemo(
    (): GridColDef<ReportTemplateDto>[] => [
      { field: nameof<ReportTemplateDto>("reportType"), headerName: "Report Type", flex: 0.3 },
      {
        field: nameof<ReportTemplateDto>("fundTypes"),
        headerName: "Fund Type",
        flex: 0.4,
        sortable: false,
        renderCell: ({ row, rowNode }) => {
          if (rowNode.type === "group") {
            return null;
          }
          return !row?.fundTypes || row?.fundTypes?.length === 0 ? (
            "-"
          ) : (
            <InlineItemsList<string>
              displayCount={1}
              items={row.fundTypes.map((f) => formatFundType(f))}
              propGetter={(prop) => prop}
            />
          );
        },
      },
      {
        field: nameof<ReportTemplateDto>("createdAt"),
        headerName: "Added At",
        flex: 0.35,
        valueFormatter: (value) => convertISODate(value),
        renderCell: (params) => (
          <Typography className="MuiDataGrid-cellContent" color={"secondary"}>
            {params.formattedValue}
          </Typography>
        ),
      },
      {
        field: nameof<ReportTemplateDto>("status"),
        headerName: "Status",
        minWidth: 80,
        flex: 0.25,
        renderCell: ({ row, rowNode }) => {
          if (rowNode.type === "group") {
            return null;
          }
          return !row.status ? "-" : row.status === Status.Live ? <LiveStatus /> : <DraftStatus />;
        },
      },
      {
        field: nameof<ReportTemplateDto>("dataSource"),
        headerName: "Data Source",
        flex: 0.3,
        renderCell: ({ row, rowNode }) => {
          if (rowNode.type === "group") {
            return null;
          }
          return <Typography className="MuiDataGrid-cellContent">{row.dataSource || "-"}</Typography>;
        },
      },
      {
        field: "actions",
        sortable: false,
        resizable: false,
        headerName: "Actions",
        width: 80,
        cellClassName: "grid-row-actions",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          if (params.rowNode.type === "group") {
            return null;
          }
          return (
            <ActionMenuButton
              template={params.row}
              readonly={readonly}
              companies={companies}
              onTemplateSelected={(selected) => setCurrentTemplateId(selected?.reportId)}
            />
          );
        },
      },
    ],
    [companies, readonly]
  );

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams<ReportTemplateDto>) => {
      if (getRowNode(apiRef, params.id)?.type === "group") {
        return "";
      }
      if (params.row.reportId === currentTemplateId) {
        return "Mui-hovered";
      }
      return "";
    },
    [apiRef, currentTemplateId]
  );

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={showToolbar}
      style={{ paddingLeft: 0 }}
      fullSelection={fullSelection}
      selection={[selectedIds, () => setAllNonGroupRowsSelection(apiRef, false)]}
      actions={<OrganizationTemplateGridCustomHeader selectedIds={selectedIds} deleteTemplates={deleteTemplates} />}
    >
      <DataGrid<ReportTemplateDto>
        apiRef={apiRef}
        treeData
        getTreeDataPath={getTreeDataPath}
        columns={columns}
        rows={sortedTemplates}
        getRowId={getRowId}
        isRowSelectable={isRowSelectable}
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
        sx={(theme) => ({
          ".MuiDataGrid-columnHeaders": {
            borderColor: theme.palette.divider,
            ".MuiDataGrid-columnHeader:nth-of-type(2)": {
              p: 0,
              outline: "none",
            },

            ".MuiDataGrid-columnHeader": {
              "&--sorted": {
                " .MuiDataGrid-iconButtonContainer": {
                  visibility: "inherit",
                },
              },
              ".MuiCheckbox-root": {
                visibility: "hidden",

                "&.MuiCheckbox-indeterminate": {
                  visibility: "visible",
                },
                "&.Mui-checked": {
                  visibility: "visible",
                },
              },
              "&:focus": {
                outline: "none",
              },

              "&:hover": {
                ".MuiCheckbox-root": {
                  visibility: "visible",
                },
              },
            },
          },

          ".MuiDataGrid-row": {
            ".MuiCheckbox-root": {
              visibility: "hidden",

              "&.MuiCheckbox-indeterminate": {
                visibility: "visible",
              },
              "&.Mui-checked": {
                visibility: "visible",
              },
            },
            "&:hover, &.Mui-hovered": {
              ".MuiDataGrid-cell.grid-row-actions": {
                button: {
                  visibility: "visible",
                },
              },
              ".highlighted-action": {
                color: theme.palette.primary.main,
              },
              ".MuiCheckbox-root": {
                visibility: "visible",
              },
            },
            "&.Mui-selected": {
              ".MuiCheckbox-root": {
                visibility: "visible",
              },
            },
            ".MuiDataGrid-cell": {
              outline: "none",
              borderBottomColor: theme.palette.divider,
            },
          },
          ".MuiDataGrid-cell": {
            "&.grid-row-actions": {
              button: {
                visibility: "hidden",
              },
            },
          },

          '[data-id^="auto-generated-row-"]': {
            '[data-field="__tree_data_group__"]': {
              px: 0,
            },

            ".MuiDataGrid-cellCheckbox": {
              maxWidth: "35px !important",
              minWidth: "35px !important",
            },
          },
          '.MuiDataGrid-row:not([data-id^="auto-generated-row-"])': {
            ".MuiDataGrid-cellCheckbox": {
              maxWidth: "35px !important",
              minWidth: "35px !important",
            },
            '[data-field="__tree_data_group__"]': {
              px: 0,
            },
            ".MuiDataGrid-cellCheckbox span": {
              visibility: "hidden",
            },
          },
        })}
        groupingColDef={groupColumnDef}
        defaultGroupingExpansionDepth={1}
        showCellVerticalBorder={false}
        showColumnVerticalBorder={false}
        disableAggregation
        getRowClassName={getRowClassName}
        sortingOrder={["asc", "desc"]}
        disableMultipleColumnsSorting
        onRowSelectionModelChange={(newSelection) => setSelectedIds(getSelectedRowIdsFromModel(newSelection))}
        onSortModelChange={(model) => (sortModelRef.current = model)}
        shiftActionsLeft
        isGroupExpandedByDefault={isGroupExpandedByDefault}
      />
    </DataGridHeaderToolbarProvider>
  );
}

const getTreeDataPath: DataGridPremiumProps["getTreeDataPath"] = (row: ReportTemplateDto) => [
  row.groupName,
  row.reportId,
];

function NameColumn({
  template,
  readonly,
  companies,
  children,
}: PropsWithChildren<{
  template: ReportTemplate;
  readonly?: boolean;
  companies: ClientInfoExtended[];
}>) {
  const [openRunWithDialog, setOpenRunWithDialog] = useState<boolean>(false);

  const onRunTemplate = useCallback(() => {
    const hasNoAccessToDataSource = !companies.some((c) => c.clientCode === template.dataSource);
    if (hasNoAccessToDataSource || !template.dataSource) {
      setOpenRunWithDialog(true);
      return;
    }
    window.open(
      getEditReportTemplateUrl(template.organization, template.dataSource, template.reportId),
      "_blank",
      "noopener noreferrer"
    );
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [template]);

  return (
    <>
      <Stack direction={"row"} sx={{ width: "100%", alignItems: "center" }}>
        <AuthorizedBox permissions={["ManageOrganizationReportTemplates"]}>{children}</AuthorizedBox>
        <Typography
          noWrap
          component={"div"}
          className="highlighted-action"
          color="textPrimary"
          sx={{ width: "100%", pl: 5.3, cursor: readonly ? "default" : "pointer" }}
          onClick={() => {
            if (readonly) return;
            onRunTemplate();
          }}
        >
          <TypographyTooltipEllipsis text={template.name} />
        </Typography>
      </Stack>
      {openRunWithDialog && template && (
        <RunWithCompanyDialog
          template={template}
          companies={companies}
          getEditReportTemplateUrl={getEditReportTemplateUrl}
          onClose={() => setOpenRunWithDialog(false)}
          onRun={() => setOpenRunWithDialog(false)}
        />
      )}
    </>
  );
}
